import { ArrowRight, Bell, Mail, ShieldCheck, Smartphone } from "lucide-react";
import { useState } from "react";
import { Brand } from "../components/Brand";
import { api } from "../lib/api";
import { enablePushNotifications } from "../lib/push";

export function AuthPage({ mode }: { mode: "login" | "signup" }) {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const isSignup = mode === "signup";

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const result = await api.requestCode(email, isSignup ? username : undefined, isSignup ? displayName : undefined);
      setStep("code");
      setMessage(result.developmentCode ? `Development code: ${result.developmentCode}` : `Code sent to ${result.email}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not send a code.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const session = await api.verifyCode(email, code);
      if (session.user) location.href = `/@${session.user.username}`;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not verify that code.");
    } finally {
      setBusy(false);
    }
  }

  async function enablePush() {
    setBusy(true);
    setMessage(await enablePushNotifications());
    setBusy(false);
  }

  return (
    <section className="auth-screen">
      <div className="auth-art">
        <Brand onColor />
        <h1>{isSignup ? "Join Floptropica One" : "Welcome back"}</h1>
        <p>Email codes replace passwords. Your account gets a secure session cookie after verification.</p>
        <div className="auth-points">
          <span><ShieldCheck size={18} /> No stored passwords</span>
          <span><Smartphone size={18} /> PWA ready</span>
          <span><Bell size={18} /> Push opt-in</span>
        </div>
      </div>
      <div className="auth-panel">
        {step === "email" ? (
          <form onSubmit={(event) => void requestCode(event)}>
            <Mail size={24} />
            <h2>{isSignup ? "Create your account" : "Sign in with email"}</h2>
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="email@floptropica.one" required />
            {isSignup && <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Display name" required />}
            {isSignup && <input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="username" required />}
            <button className="primary" disabled={busy}>{busy ? "Sending" : "Send code"} <ArrowRight size={18} /></button>
          </form>
        ) : (
          <form onSubmit={(event) => void verifyCode(event)}>
            <ShieldCheck size={24} />
            <h2>Enter your code</h2>
            <input inputMode="numeric" value={code} onChange={(event) => setCode(event.target.value)} placeholder="000000" maxLength={6} required />
            <button className="primary" disabled={busy}>{busy ? "Checking" : "Verify"} <ArrowRight size={18} /></button>
            <button type="button" className="secondary" onClick={() => setStep("email")}>Use a different email</button>
          </form>
        )}
        {message && <p className={message.includes("code:") || message.includes("sent") || message.includes("enabled") ? "success" : "error"}>{message}</p>}
        <button className="push-setup" onClick={() => void enablePush()} disabled={busy}><Bell size={18} /> Enable push notifications</button>
        <a className="auth-switch" href={isSignup ? "/login" : "/signup"}>{isSignup ? "Already have an account?" : "Need an account?"}</a>
      </div>
    </section>
  );
}
